import {
  createUnifiedTheme,
  createBaseThemeOptions,
  palettes,
} from '@backstage/theme';

const brand = {
  bg: '#060A14',
  bg2: '#0C1322',
  bg3: '#131E32',
  bg4: '#1A2640',
  emerald: '#10B981',
  emeraldDark: '#047857',
  emeraldDeep: '#022C22',
  emeraldMint: '#6EE7B7',
  text: '#F1F5F9',
  sub: '#94A3B8',
  muted: '#6E7F96',
  cyan: '#06B6D4',
  red: '#EF4444',
  orange: '#F97316',
  border: 'rgba(255,255,255,0.07)',
  borderStrong: 'rgba(255,255,255,0.12)',
};

const fontDisplay = "'Clash Display', Georgia, ui-serif, serif";
const fontBody = "'Satoshi', system-ui, ui-sans-serif, sans-serif";
const fontMono = "'JetBrains Mono', ui-monospace, monospace";

// Display headings follow the brand spacing scale, not the generic tight tracking.
const displayHeading = (
  fontSize: string,
  letterSpacing: string,
  marginBottom = 8,
) => ({
  fontFamily: fontDisplay,
  fontWeight: 700,
  fontSize,
  letterSpacing,
  lineHeight: 1.05,
  marginBottom,
});

export const quodeTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      background: {
        default: brand.bg,
        paper: brand.bg2,
      },
      primary: {
        main: brand.emerald,
        dark: brand.emeraldDark,
        light: brand.emeraldMint,
        contrastText: brand.emeraldDeep,
      },
      secondary: {
        main: brand.cyan,
        contrastText: brand.emeraldDeep,
      },
      error: { main: brand.red },
      warning: { main: brand.orange },
      info: { main: brand.cyan },
      success: { main: brand.emerald },
      text: {
        primary: brand.text,
        secondary: brand.sub,
      },
      divider: brand.border,
      link: brand.emeraldMint,
      status: {
        ok: brand.emerald,
        warning: brand.orange,
        error: brand.red,
        pending: brand.muted,
        running: brand.cyan,
        aborted: brand.muted,
      },
      // Backstage renders these as page header gradients; keep them on the
      // midnight canvas so no stray purple/blue enters the palette.
      banner: {
        info: brand.cyan,
        error: brand.red,
        text: brand.text,
        link: brand.emeraldMint,
        closeButtonColor: brand.sub,
        warning: brand.orange,
      },
      navigation: {
        background: brand.bg2,
        indicator: brand.emerald,
        color: brand.sub,
        selectedColor: brand.text,
        navItem: { hoverBackground: 'rgba(16,185,129,0.08)' },
        submenu: { background: brand.bg3 },
      },
    },
    typography: {
      htmlFontSize: 16,
      fontFamily: fontBody,
      h1: displayHeading('44px', '-0.025em'),
      h2: displayHeading('34px', '-0.02em'),
      h3: displayHeading('26px', '-0.015em'),
      h4: displayHeading('20px', '-0.015em'),
      h5: displayHeading('17px', '-0.01em'),
      h6: displayHeading('15px', '-0.01em'),
    },
  }),
  defaultPageTheme: 'home',
  // A single emerald-on-midnight header treatment across every page theme keeps
  // the one-accent rule intact instead of Backstage's default per-page colours.
  pageTheme: {
    home: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
    documentation: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
    tool: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
    service: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
    website: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
    library: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
    other: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
    app: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
    apis: {
      colors: [brand.bg2, brand.bg],
      shape: 'none',
      backgroundImage: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
      fontColor: brand.text,
    },
  },
  components: {
    BackstageHeader: {
      styleOverrides: {
        header: {
          background: `linear-gradient(120deg, ${brand.bg3} 0%, ${brand.bg} 70%)`,
          borderBottom: `1px solid ${brand.border}`,
          boxShadow: 'none',
          paddingBottom: 24,
        },
        title: {
          fontFamily: fontDisplay,
          fontWeight: 700,
          letterSpacing: '-0.015em',
        },
        subtitle: { color: brand.sub, fontFamily: fontBody },
        type: {
          fontFamily: fontMono,
          fontSize: 11,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: brand.muted,
        },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        label: {
          fontFamily: fontMono,
          fontSize: 10,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: brand.muted,
        },
        value: { color: brand.text, fontWeight: 600 },
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: { borderBottom: `1px solid ${brand.border}` },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          background: brand.bg2,
          borderRight: `1px solid ${brand.border}`,
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        label: { fontWeight: 500, fontSize: 14 },
        selected: { borderLeft: `2px solid ${brand.emerald}` },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: brand.bg },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // Material-UI v4 falls back to its own #424242 dark paper when a
          // component reads background.paper before the palette applies, which
          // showed up as grey catalog cards. Pin the brand surface explicitly.
          backgroundColor: brand.bg2,
          backgroundImage: 'none',
          border: `1px solid ${brand.border}`,
        },
        rounded: { borderRadius: 14 },
        elevation1: { boxShadow: '0 1px 2px rgba(0,0,0,0.4)' },
        elevation2: { boxShadow: '0 4px 16px rgba(0,0,0,0.45)' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: brand.bg2,
          borderRadius: 14,
          border: `1px solid ${brand.border}`,
          transition: 'border-color 200ms cubic-bezier(0.4,0,0.2,1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.01em',
          // Tactile press feedback.
          transition: 'transform 120ms cubic-bezier(0.4,0,0.2,1)',
          '&:active': { transform: 'scale(0.98)' },
        },
        containedPrimary: {
          color: brand.emeraldDeep,
          boxShadow: 'none',
          '&:hover': { background: brand.emeraldMint, boxShadow: 'none' },
        },
        outlined: { borderColor: brand.borderStrong },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          background: brand.bg3,
          border: `1px solid ${brand.border}`,
          fontFamily: fontMono,
          fontSize: 11,
          letterSpacing: '0.04em',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: fontMono,
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: brand.muted,
          borderBottom: `1px solid ${brand.border}`,
        },
        root: { borderBottom: `1px solid ${brand.border}` },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.01em',
          minWidth: 'auto',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: { color: brand.emeraldMint },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: brand.bg4,
          border: `1px solid ${brand.borderStrong}`,
          fontSize: 12,
          borderRadius: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          background: brand.bg2,
        },
      },
    },
  },
});
